// "Pre-visit intake" block in the patient Information column — recalls the answers the member gave in
// booking step 01 (demo_visit_intake; encrypted + RLS-scoped + audited). Read-only; the most recent
// intakes first, each collapsible into its question/answer pairs so the provider walks in already
// knowing the situation.
import { useState } from "react";
import { useIntakeApi } from "../lib/intakeApi";
import { useAsync } from "../lib/useAsync";
import { formatDateTime } from "../lib/format";
import { CATEGORY_ICON, CATEGORY_LABEL, normalizeCategory } from "../lib/visitCategory";

// Small category badge (icon + label) so a dental/vision intake reads differently from a medical one.
// Colors come from CSS tokens (dark-mode aware). Unknown/legacy values normalize to medical.
function CategoryTag({ category }: { category: string | null | undefined }) {
  const cat = normalizeCategory(category);
  return (
    <span className={`pp-cat-tag ${cat}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: CATEGORY_ICON[cat] }} />
      {CATEGORY_LABEL[cat]}
    </span>
  );
}

export function IntakeRecall({ patientId }: { patientId: string }) {
  const api = useIntakeApi();
  const intakes = useAsync(() => api.list(patientId), [patientId]);
  const [open, setOpen] = useState<string | null>(null);

  if (intakes.loading || intakes.error) return null;
  const list = intakes.data ?? [];

  return (
    <div className="pp-info-block">
      <h4>Pre-visit intake <span className="pp-count">({list.length})</span></h4>
      {list.length ? (
        <div className="pp-intake-list">
          {list.map((it, i) => {
            const expanded = open === it.id || (open === null && i === 0);
            return (
              <div className={`pp-intake-item ${expanded ? "open" : ""}`} key={it.id}>
                <button
                  type="button"
                  className="pp-intake-head"
                  onClick={() => setOpen(expanded ? "" : it.id)}
                >
                  <span className="pp-intake-titlewrap">
                    <CategoryTag category={it.category} />
                    <span className="pp-intake-title">{it.form_title}</span>
                  </span>
                  <span className="pp-intake-date">{formatDateTime(it.created_at)}</span>
                </button>
                {expanded && (
                  <dl className="pp-intake-qa">
                    {it.responses.length ? (
                      it.responses.map((r, j) => (
                        <div className="pp-intake-qa-row" key={j}>
                          <dt>{r.q}</dt>
                          <dd>{r.a}</dd>
                        </div>
                      ))
                    ) : (
                      <div className="pp-intake-qa-row"><dd className="muted">No details captured.</dd></div>
                    )}
                  </dl>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <span className="pp-info-text muted">No intake submitted yet.</span>
      )}
    </div>
  );
}
